import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useKeycloakToken } from '../common/useKeycloak';
import { DraftReferenceState } from '../../api/referenceOptions';
import { loadOrganizations } from '../../api/organizations/loadOrganizations';
import DestinationModal from './DestinationModal';
import { addDestination } from '../../api/destinations/destinationsApi';
import { loadDestinationTypes as loadDestinationTypes_all } from '../../api/destinations/loadDestinationTypes';
import {
  addRouteDestinations,
  getIntegrationDetails,
  getRoute,
  getRouteDetails,
} from '../../api/routes/routesApi';
import { routeRecordId } from '../routes/routeDisplayName';
import { toast } from 'react-toastify';
import {
  NewIntegration,
  DestinationType,
  Organization,
  buildNewIntegration,
  buildDestinationType,
  HIDE_TECHNOLOGY_CARD_SENTINEL,
} from '../common/ObjectTypes';
import { filter_page_size } from '../../api/apiUtils';
import { useTranslation } from 'react-i18next';
import { baseUrlIsRequired } from '../routes/providerBaseUrl';
import {
  trackDestinationAdded,
  trackDestinationCreationStarted,
  trackDestinationCreationAbandoned,
} from '../../analytics/analytics';
import {
  isEarthRangerDestinationType,
  constructEarthRangerUrl,
} from './earthRangerUrl';
import { buildDraftReference } from '../common/buildDraftReference';
import { loadCurrentUserDetails } from '../organizations/loadCurrentUserDetails';
import { getRouteKind } from '../common/routeKind';
import { skeletonClasses, wizardClasses } from '../common/tailwindClassnames';
import { isValidRouteSubpage } from '../routes/routeSubpages';
import NotFoundPage from '../common/NotFoundPage';
import { validateBaseUrl } from './validateBaseUrl';
import {
  ActionConfigErrors,
  configurationsForCreate,
  validateActionConfigs,
} from '../common/SchemaFormTemplates/validateActionConfigs';
import {
  connectionSetupActions,
  destinationSetupActions,
} from '../common/SchemaFormTemplates/actionFilters';
import { actionIsSentOnCreate } from '../common/configPanel/actionSetup';

type ManageDestinationModalProps = {
  onToggleModal: Function;
  onAddNewDestination?: (dest: any) => void;
};

export function ManageDestinationModal({
  onToggleModal,
  onAddNewDestination,
}: ManageDestinationModalProps) {
  const params = useParams();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const [token] = useKeycloakToken();
  const { t } = useTranslation();
  const [currentMemberIsAdmin, setCurrentMemberIsAdmin] =
    useState<boolean>(false);
  const [pageLoaded, setPageLoaded] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgSearchInput, setOrgSearchInput] = useState<string>('');
  const [reloadOrgs, setReloadOrgs] = useState<boolean>(true);
  const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>(
    [],
  );
  // The technology grid is the first thing this screen shows, so it needs to
  // tell "still loading" apart from "nothing to offer" — an empty list would
  // otherwise read as no results while the fetch is still in flight.
  const [typesLoaded, setTypesLoaded] = useState<boolean>(false);
  // The fetch failed outright. Kept apart from an empty list so the grid can
  // say "could not load" instead of sending the user to adjust a search.
  const [typesError, setTypesError] = useState<boolean>(false);
  const [destination, setDestination] = useState<NewIntegration>(
    buildNewIntegration(false),
  );
  const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
  const [destinationType, setDestinationType] = useState<DestinationType>(
    buildDestinationType(),
  );
  const [errors, setErrors] = useState({});
  // Keyed by action id: { [action_id]: { [field]: message } }.
  const [rsjfErrors, setRsjfErrors] = useState<ActionConfigErrors>({});
  // True once the user edits any field after picking a technology type;
  // picking (or switching) the type resets it.
  const [changesMade, setChangesMade] = useState(false);
  const isEarthRanger = isEarthRangerDestinationType(destinationType.name);

  // cdip's IntegrationTypeView uses `lookup_field = "value"` (slug), not
  // the UUID id. Sending the id would 404 on every draft fetch.
  // EarthRanger destinations store the site as a slug and only expand to a
  // full URL on save, but the runner authenticates against a real URL.
  const draftReference: DraftReferenceState | undefined = useMemo(
    () =>
      buildDraftReference({
        type: destinationType,
        owner: destination.owner,
        baseUrl: destination.base_url,
        updatedConfigurations,
      }),
    [
      destinationType,
      destination.owner,
      destination.base_url,
      updatedConfigurations,
    ],
  );

  const toggleModalMouseEvent: React.MouseEventHandler<
    HTMLButtonElement
  > = () => {
    trackDestinationCreationAbandoned(
      destinationType?.name || undefined,
      organizations.find((o: Organization) => o.id === destination.owner)
        ?.name || undefined,
    );
    onToggleModal();
  };
  const navigate = useNavigate();

  // The /routes/:route_id/:subpage/newdestination route accepts any
  // :subpage string — an unknown value should 404 like the parent page does
  const invalidSubpage = !isValidRouteSubpage(params.subpage);

  // Launched from a Route, this modal creates a DESTINATION for it, so only
  // technologies with a push action belong in the list, ungrouped — a "Can send
  // data to Gundi" group makes no sense when nobody is looking for a provider.
  // DO NOT remove this filter or "simplify" the param: the URL param is
  // :route_id (it was :connection_id before the Routes rename, and the filter
  // silently died once already when this check kept the old name). Launched
  // from the Connections list it creates an integration in its own right,
  // which may be a source — that list stays unfiltered.
  const forRouteDestination = !!params.route_id;

  useEffect(() => {
    if (invalidSubpage) return;
    // The previous page may have been scrolled down (e.g. the create-new
    // card below the destinations list); start this view at the top
    window.scrollTo({ top: 0 });
    trackDestinationCreationStarted();
  }, [invalidSubpage]);

  useEffect(() => {
    if (invalidSubpage) return;
    const loadOrganizationsData = async () => {
      if (!keycloak.token) return;
      try {
        if (orgSearchInput) {
          const [orgs, , error] = await loadOrganizations(
            keycloak.token,
            0,
            orgSearchInput,
            [],
          );
          if (!error) setOrganizations(orgs);
        } else {
          let accumulated: Organization[] = [];
          let offset = 0;
          while (true) {
            const [orgs, total, error] = await loadOrganizations(
              keycloak.token,
              offset,
              '',
              accumulated,
            );
            if (error) break;
            accumulated = orgs;
            if (offset + filter_page_size >= total) break;
            offset += filter_page_size;
          }
          setOrganizations(accumulated);
        }
        setReloadOrgs(false);
      } catch (err) {
        console.log('Failed to load organizations:', err);
      }
    };
    const loadDestinationTypes = async () => {
      if (keycloak.token) {
        try {
          // Every technology, not just the first page of them: the endpoint
          // pages at page_size, and a type past page one would silently be
          // impossible to choose.
          let accumulated: DestinationType[] = [];
          let typesOffset = 0;
          let failed = false;
          for (let page = 0; page < 25; page += 1) {
            const [types, total, error] = await loadDestinationTypes_all(
              keycloak.token,
              typesOffset,
              '',
              accumulated,
              !forRouteDestination,
            );
            // ANY page failing is a failed load, not a short list. A
            // partial list looks settled and complete, so every
            // technology on the failed page and after it silently
            // becomes impossible to choose — worse than saying so.
            if (error) {
              failed = true;
              break;
            }
            accumulated = types;
            if (typesOffset + filter_page_size >= total) break;
            typesOffset += filter_page_size;
          }
          // Technologies marked with the hide-card sentinel are not on
          // offer in a technology grid — same filter ManageAddRouteStepOne
          // applies to provider types.
          setDestinationTypes(
            accumulated.filter(
              (dt: DestinationType) =>
                dt.description !== HIDE_TECHNOLOGY_CARD_SENTINEL,
            ),
          );
          setTypesError(failed);
        } catch (err) {
          console.log(err);
          setTypesError(true);
        } finally {
          // Even on failure: the grid must stop showing skeletons and
          // say there is nothing to pick.
          setTypesLoaded(true);
        }
      }
    };
    loadDestinationTypes();
    if (reloadOrgs) loadOrganizationsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.authenticated, reloadOrgs, forRouteDestination]);

  async function handleSelectOrg(event: any) {
    setCurrentMemberIsAdmin(
      await loadCurrentUserDetails(
        event.value,
        keycloak.token ? keycloak.token : '',
      ),
    );
    setChangesMade(true);
    setDestination((prevDest: NewIntegration) => ({
      ...prevDest,
      owner: event.value,
    }));
  }
  // Preselect the technology when launched from a technology tab (bidirectional
  // redesign): route state carries the type id so the user lands in configuration.
  useEffect(() => {
    const preId = (location.state as any)?.preselectTypeId;
    if (!preId || destinationType.id || destinationTypes.length === 0) return;
    const full = destinationTypes.find(
      (dt: DestinationType) => dt.id === preId,
    );
    if (full) handleSelectType(full);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationTypes]);

  const handleSelectType = (event: any) => {
    // Reset type-specific state
    setUpdatedConfigurations([]);
    setErrors({});
    setRsjfErrors({});

    // Update destination type
    const fullType = destinationTypes.find((t) => t.id === event.id);
    const resolved = fullType ?? {
      id: event.id,
      name: event.name,
      description: event.description,
      value: event.value,
      actions: event.actions,
    };
    setDestinationType(resolved);

    // Update destination with new type and clear configurations.
    // `type` is the integration type's ID in the POST body — NOT its `value`,
    // which is the technology slug ("earth_ranger") used for logo lookup.
    // Read it off the record instead of off a caller-supplied `value`: the
    // old dropdown had to overwrite `value` with the id to make this work,
    // and both callers that pass a real DestinationType (the technology grid
    // and the preselect-from-tab effect) silently sent the slug instead.
    setDestination((prevDest: NewIntegration) => ({
      ...prevDest,
      type: resolved.id,
      configurations: [], // Clear previous configurations
      base_url: '', // Reset URL since it might have different requirements per type
    }));
  };

  // Back to the technology grid. Everything typed so far belonged to the
  // technology being abandoned, so it goes with it — same reset
  // `handleSelectType` performs when the type changes.
  const handleClearType = () => {
    setUpdatedConfigurations([]);
    setErrors({});
    setRsjfErrors({});
    // `changesMade` deliberately survives: Name and Workspace are not
    // technology-specific and keep their values, so clearing the flag would
    // leave a filled-in form with Create disabled until the user re-touched
    // a field for no reason.
    setDestinationType(buildDestinationType());
    setDestination((prevDest: NewIntegration) => ({
      ...prevDest,
      type: '',
      configurations: [],
      base_url: '',
    }));
  };

  // The sections whose required fields count: what the screen shows for this
  // record's role, minus anything switched off. Same predicate the payload is
  // built from, so the screen validates exactly what it sends.
  const enabledSetupActions = () =>
    (forRouteDestination
      ? destinationSetupActions(destinationType.actions)
      : connectionSetupActions(destinationType.actions)
    ).filter((action: any) =>
      actionIsSentOnCreate(
        action,
        updatedConfigurations.find((c: any) => c.action === action.id)?.data,
      ),
    );

  const formIsValid = () => {
    let form_errors: any = {};
    if (destination.name.length === 0)
      form_errors.name = t('Name is required.');
    const shouldRequireUrl = baseUrlIsRequired(destinationType.value);
    const urlError = validateBaseUrl({
      value: destination.base_url,
      asSiteName: isEarthRanger,
      optional: !shouldRequireUrl,
      t,
    });
    if (urlError) form_errors.base_url = urlError;
    if (destination.owner === '')
      form_errors.owner = t('Select an Organization.');
    setErrors(form_errors);
    // Every section the screen shows, minus the ones switched off by their own
    // toggle: all of them are on the page now, so a required field left empty
    // is visible work the user owes rather than something to fill in later
    // (GUNDI-5697, same rule as the Route wizard).
    const rsjf_errors = validateActionConfigs(
      enabledSetupActions(),
      updatedConfigurations,
      t,
    );
    setRsjfErrors({ ...rsjf_errors });
    return (
      Object.keys(form_errors).length === 0 &&
      Object.keys(rsjf_errors).length === 0
    );
  };

  // action: 'save' | 'saveAndClose'
  async function handleAddDestination(
    e: React.FormEvent<Element>,
    action: 'save' | 'saveAndClose' = 'save',
  ) {
    const isRouteAddFlow =
      location.pathname.includes('/routes/new/') && params.route_id;
    const isRouteDetailsFlow =
      location.pathname.includes('/routes/') && params.route_id;
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const isFormValid = formIsValid();
    if (!isFormValid) {
      console.warn('Form is invalid in handleAddDestination.');
      // The blocking fields can sit in a section the user never opened, so
      // say so out loud — the section itself carries the "!" badge, and we
      // deliberately do not move the user there.
      toast.error(
        t(
          'There are fields that require your attention. Please check and correct the errors.',
        ),
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    if (!keycloak.token) {
      console.warn('No keycloak token in handleAddDestination.');
      return;
    }

    try {
      // File-widget values go to the backend as JSON strings; done here, on
      // the payload, rather than by rewriting the form's state.
      // From the edit state, not from `destination.configurations`: one
      // source of truth for what the user actually filled in.
      // Switched-off sections are left out — see configurationsForCreate.
      const configurations = configurationsForCreate(
        destinationType.actions,
        updatedConfigurations,
      );
      const destinationToSave = isEarthRanger
        ? {
            ...destination,
            configurations,
            base_url: constructEarthRangerUrl(destination.base_url),
          }
        : { ...destination, configurations };
      let temp = await addDestination(keycloak.token, destinationToSave);
      if (!temp || !temp.id) {
        console.error(
          'addDestination did not return a valid destination or id:',
          temp,
        );
        return;
      }
      toast.success(`${t('Destination added')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      trackDestinationAdded(
        params.route_id,
        destinationType?.name,
        organizations.find((o: Organization) => o.id === destination.owner)
          ?.name,
      );
      let routeKind = 'push-only';
      if (isRouteAddFlow || isRouteDetailsFlow) {
        try {
          // The URL carries the provider INTEGRATION id, so the kind
          // lookup can use it directly — but the PATCH below targets the
          // route RECORD, which holds this route's own destinations, so
          // resolve that from the provider's connection first.
          const providerIntegrationId = params.route_id;
          if (providerIntegrationId) {
            try {
              const provider = await getIntegrationDetails(
                keycloak.token,
                providerIntegrationId,
              );
              if (provider?.type) routeKind = getRouteKind(provider.type);
            } catch (kindErr) {
              console.error('failed to resolve provider kind', kindErr);
            }
          }
          const conn = await getRouteDetails(
            keycloak.token,
            providerIntegrationId || '',
          );
          const recordId = routeRecordId(conn);
          if (!recordId) throw new Error('provider has no route record');
          const record = await getRoute(keycloak.token, recordId);
          const existingDestIds = Array.isArray(record?.destinations)
            ? record.destinations.map((d: any) => d.id)
            : [];
          const allDestIds = [...existingDestIds, temp.id];
          await addRouteDestinations(keycloak.token, record.id, {
            destinations: allDestIds,
          });
          toast.success(`${t('Route updated')}`, {
            position: toast.POSITION.TOP_RIGHT,
          });
        } catch (fetchError: any) {
          console.error(
            'Error associating destination with route:',
            fetchError,
          );
          // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
          if (fetchError?.response?.status !== 401) {
            toast.error(
              t('Something went wrong. Please try again or contact support.'),
              { position: toast.POSITION.TOP_RIGHT },
            );
          }
        }
      }
      // case 1: add Route flow — navigate to provider or webhook tab (destination is already linked)
      if (isRouteAddFlow) {
        const targetTab = routeKind === 'webhook' ? 'webhook' : 'provider';
        navigate(`/routes/${params.route_id}/${targetTab}`, {
          replace: true,
          state: { fromAddRouteFlow: true },
        });
      }
      // case 2: Route details flow — land on the flow map with the new
      // destination's node selected (the destinations tab no longer exists)
      else if (
        (action === 'save' || action === 'saveAndClose') &&
        isRouteDetailsFlow
      ) {
        navigate(`/routes/${params.route_id}`, {
          replace: true,
          state: { flowSelection: { kind: 'account', integrationId: temp.id } },
        });
      }
      // case 3: adding dest from destinations list page
      else if (action === 'save' && !isRouteAddFlow && !isRouteDetailsFlow) {
        // navigate to destination details page
        goToDetails(temp.id);
      } else if (
        action === 'saveAndClose' &&
        !isRouteAddFlow &&
        !isRouteDetailsFlow
      ) {
        // simply close the modal
        navigate(-1);
      }
    } catch (error: any) {
      console.error('Error in handleAddDestination:', error);
      // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
      if (error?.response?.status === 401) return;
      toast.error(
        t('Something went wrong. Please try again or contact support.'),
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  }

  function goToDetails(destination_id: string) {
    // A Connection that was just created has no Routes yet — land on the tab
    // where there is something to do, not on an empty list.
    navigate(`/connections/${destination_id}/configuration`);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const item = event.target;
    const item_id: string = item.id;
    setChangesMade(true);
    setDestination((prevDest: NewIntegration) => ({
      ...prevDest,
      [item_id]: item.value,
    }));
  }

  function handleChangeJSONForm(
    e: Record<string, any> | undefined,
    action_id: string,
    fieldId?: string,
  ) {
    // fieldId is absent on rjsf's initial default-population onChange —
    // only user-originated edits count as changes.
    if (fieldId) setChangesMade(true);
    if (!e) return;
    const newConfig = { action: action_id, data: e };
    // One array, built once and used for both. The old code queued the new
    // entry with a functional update and then wrote THIS render's stale array
    // into `destination`, so an action edited exactly once was configured in
    // the form and absent from the POST. It only appeared to work for later
    // edits because those mutated the array in place — which also meant React
    // saw the same reference and skipped the re-render.
    const index_to_find = updatedConfigurations.findIndex(
      (a: any) => a.action === action_id,
    );
    const next =
      index_to_find === -1
        ? [...updatedConfigurations, newConfig]
        : updatedConfigurations.map((c: any, i: number) =>
            i === index_to_find ? newConfig : c,
          );
    setUpdatedConfigurations(next);
    setDestination((prevRoute: any) => ({
      ...prevRoute,
      configurations: next,
    }));
  }

  function handleSearchOrgDropdown(e: any) {
    setOrgSearchInput(e);
    if (e.length > 2 || e.length === 0) setReloadOrgs(true);
  }

  if (invalidSubpage) return <NotFoundPage />;

  return !pageLoaded ? (
    <div className="z-20 flex min-h-screen flex-col">
      <div className={wizardClasses.band}>
        <div className={wizardClasses.row}>
          <h1 className={wizardClasses.title}>{t('New Connection')}</h1>
        </div>
      </div>
      <div className={wizardClasses.body}>
        <div className={wizardClasses.row}>
          <div>
            <div className="flex flex-col gap-6 px-4 sm:px-6">
              <div className="flex flex-col gap-2">
                <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                <div
                  className={`h-10 w-full max-w-md ${skeletonClasses.base}`}
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                <div
                  className={`h-10 w-full max-w-md ${skeletonClasses.base}`}
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className={`h-4 w-40 ${skeletonClasses.base}`} />
                <div
                  className={`h-10 w-full max-w-md ${skeletonClasses.base}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <DestinationModal
      currentMemberIsAdmin={currentMemberIsAdmin}
      errors={errors}
      rsjfErrors={rsjfErrors}
      onToggleModal={toggleModalMouseEvent}
      destination={destination}
      destinationType={destinationType}
      organizations={organizations}
      loadMoreOrgs={() => {}}
      orgInputChange={handleSearchOrgDropdown}
      orgSearchActive={!!orgSearchInput}
      destinationTypes={destinationTypes}
      typesLoaded={typesLoaded}
      typesError={typesError}
      onClearType={handleClearType}
      receiveOnly={forRouteDestination}
      onChange={handleChange}
      onChangeOrg={handleSelectOrg}
      onChangeType={handleSelectType}
      onChangeJSONForm={handleChangeJSONForm}
      configurations={updatedConfigurations}
      onSave={handleAddDestination}
      onGoToDetails={goToDetails}
      changesMade={changesMade}
      draftReference={draftReference}
      authToken={typeof token === 'string' ? token : undefined}
    />
  );
}
